import React, { useState, useRef, useCallback } from 'react';
import {
    Box, Paper, Toolbar, Typography, Tooltip, IconButton, Divider, SxProps, Theme,
    FormControlLabel,
    Checkbox,
    alpha
} from '@mui/material';
import {
    Delete as DeleteIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    Refresh as RefreshIcon,
    PanTool as PanToolIcon,
} from '@mui/icons-material';
import { AnnotatedSection, TableData, useOCRStore } from '../../store/ocrStore';
import Button from '../../../../core-components/theme/components/Button/Button';

const styles: { [key: string]: SxProps<Theme> } = {
    viewport: {
        width: '100%',
        minHeight: '300px',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
    },
    content: {
        position: 'relative',
        width: '100%',
        height: '100%',
        transformOrigin: '0 0',
    },
    image: {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        pointerEvents: 'none',
    },
    boundingBox: {
        position: 'absolute',
        border: '1.5px solid',
        borderColor: 'error.main',
        backgroundColor: 'rgba(239, 83, 80, 0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderRadius: '2px',
        boxSizing: 'border-box',
        overflow: 'scroll'
    },
    selectedBox: {
        borderColor: 'primary.main',
        backgroundColor: 'rgba(25, 118, 210, 0.3)',
        borderWidth: '2.5px',
        boxShadow: (theme) => `0 0 8px ${theme.palette.primary.light}`,
        zIndex: 1,
    },
    coverageBox: {
        backgroundColor: 'rgba(239, 83, 80, 0.7)',
        borderColor: 'rgba(239, 83, 80, 0.9)',
    },
    sensorPlacedBox: {
        backgroundColor: (theme) => alpha(theme.palette.Greeny.main, 1),
        borderColor: (theme) => theme.palette.Greeny.dark,
        borderWidth: '2px',
        borderStyle: 'solid',
    }
};

interface DocumentPreviewProps {
    annotationData: AnnotatedSection;
    onDelete?: () => void;
    title?: string;
    tableData?: TableData | null;
}

interface RenderBoundingBoxesParams {
    tableData: TableData | null | undefined;
    selectedBlockId: string | null;
    showCoverage: boolean;
    handleBoxClick: (e: React.MouseEvent, blockId: string) => void;
    styles: { [key: string]: SxProps<Theme> };
}

const renderBoundingBoxes = ({
    tableData,
    selectedBlockId,
    showCoverage,
    handleBoxClick,
    styles,
}: RenderBoundingBoxesParams) => {
    if (!tableData?.grid) return null;
    return tableData.grid.flat().map(cell => {
        if (!cell || 'spanned' in cell) return null;
        const isSelected = cell.id === selectedBlockId;
        const isCovered = showCoverage && !isSelected;
        const isSensorPlaced = !!cell.sensor;
        return (
            <Box
                key={cell.id}
                sx={{
                    ...styles.boundingBox,
                    ...(isSelected ? styles.selectedBox : {}),
                    ...(isCovered ? styles.coverageBox : {}),
                    ...(isSensorPlaced ? styles.sensorPlacedBox : {}),
                    top: `${cell.geometry.BoundingBox.Top * 100}%`,
                    left: `${cell.geometry.BoundingBox.Left * 100}%`,
                    width: `${cell.geometry.BoundingBox.Width * 100}%`,
                    height: `${cell.geometry.BoundingBox.Height * 100}%`,
                } as SxProps<Theme>}
                onClick={(e: React.MouseEvent) => handleBoxClick(e, cell.id)}
            >
                {isSensorPlaced && (
                    <Typography
                        variant="caption"
                        sx={{
                            width: '100%',
                            height: '100%',
                            fontWeight: 900,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: (theme) => theme.palette.primary.main,
                            pointerEvents: 'none',
                            zIndex: 2,
                            padding: 1
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1,
                                fontWeight: 500,
                                color: 'text.secondary',
                                fontSize: '0.85rem',
                            }}
                        >
                            {String(cell.value)}
                        </Typography>
                        {cell.sensor?.sensor}
                    </Typography>
                )}
            </Box>
        );
    });
};

const DocumentPreviewInternal: React.FC<DocumentPreviewProps> = ({
    annotationData,
    onDelete,
    title,
    tableData,
}) => {
    const { selectedBlockId, setSelectedBlockId, showCoverage, toggleCoverage } =
        useOCRStore(state => ({
            selectedBlockId: state.selectedBlockId,
            setSelectedBlockId: state.setSelectedBlockId,
            showCoverage: state.showCoverage,
            toggleCoverage: state.toggleCoverage,
        }));
    const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
    const [isPanMode, setIsPanMode] = useState(false);
    const { id: annotationId, imageData: imageUrl } = annotationData;
    const isCoverageOnForThisPreview = showCoverage.has(annotationId);
    const panStartRef = useRef<{ startX: number, startY: number, tx: number, ty: number } | null>(null);
    const viewportRef = useRef<HTMLDivElement>(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    const handleBoxClick = useCallback((e: React.MouseEvent, blockId: string) => {
        e.stopPropagation();
        setSelectedBlockId(selectedBlockId === blockId ? null : blockId);
    }, [selectedBlockId, setSelectedBlockId]);

    const onPanStart = useCallback((e: React.MouseEvent) => {
        if (!isPanMode || !viewportRef.current) return;
        e.preventDefault();
        panStartRef.current = { startX: e.clientX, startY: e.clientY, tx: transform.x, ty: transform.y };
        viewportRef.current.style.cursor = 'grabbing';
    }, [isPanMode, transform.x, transform.y]);

    const onPanMove = useCallback((e: React.MouseEvent) => {
        const panStart = panStartRef.current;
        if (!panStart) return;
        e.preventDefault();
        const dx = e.clientX - panStart.startX;
        const dy = e.clientY - panStart.startY;
        setTransform(prev => ({ ...prev, x: panStart?.tx + dx, y: panStart?.ty + dy }));
    }, [transform.scale]);

    const onPanEnd = useCallback(() => {
        if (panStartRef.current && viewportRef.current) {
            panStartRef.current = null;
            viewportRef.current.style.cursor = 'grab';
        }
    }, []);

    const adjustZoom = (zoomDirection: 'in' | 'out') => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const zoomFactor = 1.25;
        const { width, height } = viewport.getBoundingClientRect();
        const centerX = width / 2;
        const centerY = height / 2;

        setTransform(prev => {
            const newScale = Math.max(0.5, Math.min(zoomDirection === 'in' ? prev.scale * zoomFactor : prev.scale / zoomFactor, 5));
            const newX = centerX - (centerX - prev.x) * (newScale / prev.scale);
            const newY = centerY - (centerY - prev.y) * (newScale / prev.scale);
            return { scale: newScale, x: newX, y: newY };
        });
    };

    const resetTransform = () => setTransform({ scale: 1, x: 0, y: 0 });

    return (
        <Paper elevation={0} sx={{ mb: 2 }}>
            <Toolbar
                sx={{
                    p: { xs: 1, sm: 1 },
                    minHeight: 'auto !important',
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={isCoverageOnForThisPreview}
                                onChange={() => toggleCoverage(annotationData.id)} // Toggle this specific ID
                            />
                        }
                        label={<Typography variant="body2">Show Coverage</Typography>}
                        sx={{ mr: 1 }}
                    />
                    <Divider orientation="vertical" flexItem />
                    <Tooltip title="Zoom Out" arrow>
                        <IconButton onClick={() => adjustZoom('out')} size="small">
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset View" arrow>
                        <IconButton onClick={resetTransform} size="small">
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Zoom In" arrow>
                        <IconButton onClick={() => adjustZoom('in')} size="small">
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Tooltip title="Toggle Pan Mode" arrow>
                    <Button
                        onClick={() => setIsPanMode(!isPanMode)}
                        variant={isPanMode ? 'contained' : 'outlined'}
                        size="small"
                        color={isPanMode ? 'warning' : 'primary'}
                        startIcon={<PanToolIcon fontSize="small" />}
                        sx={{ mr: 1 }}
                    >
                        Pan
                    </Button>
                </Tooltip>
                {onDelete && <Tooltip title="Delete" arrow><IconButton onClick={onDelete} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
            </Toolbar>
            <Box
                ref={viewportRef}
                sx={{ ...styles.viewport, cursor: isPanMode ? 'grab' : 'default' }}
                onMouseDown={onPanStart}
                onMouseMove={onPanMove}
                onMouseUp={onPanEnd}
                onMouseLeave={onPanEnd}
            >
                <Box sx={{ ...styles.content, transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})` }}>
                    <img src={imageUrl} alt={title || 'Document Preview'} style={styles.image as React.CSSProperties} onLoad={() => setImageLoaded(true)} />
                    {imageLoaded && renderBoundingBoxes({
                        tableData,
                        selectedBlockId,
                        showCoverage: isCoverageOnForThisPreview,
                        handleBoxClick,
                        styles,
                    })}
                </Box>
            </Box>
        </Paper>
    );
};

export const DocumentPreview = React.memo(DocumentPreviewInternal);